import { diffLines, diffWordsWithSpace } from 'diff';

export type TextDiffLineKind = 'unchanged' | 'removed' | 'added';

export interface TextSegment {
  text: string;
  changed: boolean;
  start: number;
}

export interface TextDiffLine {
  lineNumber: number;
  text: string;
  kind: TextDiffLineKind;
  segments?: TextSegment[];
}

export interface TextDiffRow {
  left?: TextDiffLine;
  right?: TextDiffLine;
}

interface TextDiffRowBuilder {
  rows: TextDiffRow[];
  leftLineNumber: number;
  rightLineNumber: number;
  unpairedRemovalIndex: number;
}

interface WordChange {
  value: string;
  added: boolean;
  removed: boolean;
}

export const MAX_TEXT_DIFF_CHARACTERS = 1024 * 1024;

const MAX_WORD_DIFF_EDIT_LENGTH = 200;

export const exceedsTextDiffLimit = (text: string): boolean => text.length > MAX_TEXT_DIFF_CHARACTERS;

const splitLines = (value: string): string[] => value.replace(/\n$/, '').split('\n');

const toSegments = (changes: WordChange[], changedFlag: 'added' | 'removed'): TextSegment[] => {
  const segments: TextSegment[] = [];
  let start = 0;
  for (const change of changes) {
    segments.push({ text: change.value, changed: change[changedFlag], start });
    start += change.value.length;
  }
  return segments;
};

export const buildWordSegments = (leftText: string, rightText: string) => {
  const changes = diffWordsWithSpace(leftText, rightText, { maxEditLength: MAX_WORD_DIFF_EDIT_LENGTH });
  if (!changes) {
    return;
  }
  return {
    left: toSegments(
      changes.filter(change => !change.added),
      'removed'
    ),
    right: toSegments(
      changes.filter(change => !change.removed),
      'added'
    ),
  };
};

const highlightChangedWords = (left: TextDiffLine, right: TextDiffLine) => {
  const segments = buildWordSegments(left.text, right.text);
  if (segments) {
    left.segments = segments.left;
    right.segments = segments.right;
  }
};

const appendRemoved = (builder: TextDiffRowBuilder, text: string) => {
  builder.leftLineNumber += 1;
  builder.rows.push({ left: { lineNumber: builder.leftLineNumber, text, kind: 'removed' } });
};

const appendAdded = (builder: TextDiffRowBuilder, text: string) => {
  builder.rightLineNumber += 1;
  const right: TextDiffLine = { lineNumber: builder.rightLineNumber, text, kind: 'added' };
  const unpairedRemoval = builder.rows[builder.unpairedRemovalIndex];
  if (unpairedRemoval?.left) {
    unpairedRemoval.right = right;
    highlightChangedWords(unpairedRemoval.left, right);
    builder.unpairedRemovalIndex += 1;
    return;
  }
  builder.rows.push({ right });
  builder.unpairedRemovalIndex = builder.rows.length;
};

const appendUnchanged = (builder: TextDiffRowBuilder, text: string) => {
  builder.leftLineNumber += 1;
  builder.rightLineNumber += 1;
  builder.rows.push({
    left: { lineNumber: builder.leftLineNumber, text, kind: 'unchanged' },
    right: { lineNumber: builder.rightLineNumber, text, kind: 'unchanged' },
  });
  builder.unpairedRemovalIndex = builder.rows.length;
};

const appenders = {
  removed: appendRemoved,
  added: appendAdded,
  unchanged: appendUnchanged,
};

const getLineKind = ({ added, removed }: { added: boolean; removed: boolean }): TextDiffLineKind => {
  if (added) {
    return 'added';
  }
  return removed ? 'removed' : 'unchanged';
};

const TEXT_DIFF_TIMEOUT_MS = 2000;

export const buildTextDiffRows = (
  leftText: string,
  rightText: string,
  { timeoutMs = TEXT_DIFF_TIMEOUT_MS }: { timeoutMs?: number } = {}
): TextDiffRow[] | undefined => {
  const changes = diffLines(leftText, rightText, {
    stripTrailingCr: true,
    ignoreNewlineAtEof: true,
    timeout: timeoutMs,
  });
  if (!changes) {
    return undefined;
  }

  const builder: TextDiffRowBuilder = { rows: [], leftLineNumber: 0, rightLineNumber: 0, unpairedRemovalIndex: 0 };
  for (const change of changes) {
    const append = appenders[getLineKind(change)];
    for (const text of splitLines(change.value)) {
      append(builder, text);
    }
  }

  return builder.rows;
};

export const countTextDiffChanges = (rows: TextDiffRow[]) => ({
  added: rows.filter(row => row.right?.kind === 'added').length,
  removed: rows.filter(row => row.left?.kind === 'removed').length,
});

export interface UnifiedDiffLine {
  kind: TextDiffLineKind;
  text: string;
  leftLineNumber?: number;
  rightLineNumber?: number;
  segments?: TextSegment[];
}

interface PendingChanges {
  removed: UnifiedDiffLine[];
  added: UnifiedDiffLine[];
}

const flushPendingChanges = (lines: UnifiedDiffLine[], pending: PendingChanges) => {
  for (const line of [...pending.removed, ...pending.added]) {
    lines.push(line);
  }
  pending.removed = [];
  pending.added = [];
};

const addChangedRow = (pending: PendingChanges, { left, right }: TextDiffRow) => {
  if (left) {
    pending.removed.push({
      kind: 'removed',
      text: left.text,
      leftLineNumber: left.lineNumber,
      segments: left.segments,
    });
  }
  if (right) {
    pending.added.push({
      kind: 'added',
      text: right.text,
      rightLineNumber: right.lineNumber,
      segments: right.segments,
    });
  }
};

export const buildUnifiedLines = (rows: TextDiffRow[]): UnifiedDiffLine[] => {
  const lines: UnifiedDiffLine[] = [];
  const pending: PendingChanges = { removed: [], added: [] };

  for (const row of rows) {
    if (row.left?.kind === 'unchanged') {
      flushPendingChanges(lines, pending);
      lines.push({
        kind: 'unchanged',
        text: row.left.text,
        leftLineNumber: row.left.lineNumber,
        rightLineNumber: row.right?.lineNumber,
      });
    } else {
      addChangedRow(pending, row);
    }
  }

  flushPendingChanges(lines, pending);
  return lines;
};
